import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage } from '@/lib/firebase'
import type { ImageUploadPreset } from '@/lib/optimizeImage'
import { isSupportedImageFile } from '@/lib/optimizeImage'

const CONTACT_MAX_FILE_BYTES = 5 * 1024 * 1024
const CONTACT_ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
])

const CONTACT_EXT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  pdf: 'application/pdf',
}

function inferContactContentType(file: File): string {
  if (file.type && CONTACT_ALLOWED_TYPES.has(file.type)) {
    return file.type
  }
  const ext = file.name.split('.').pop()?.toLowerCase()
  return CONTACT_EXT_TYPES[ext ?? ''] ?? 'application/octet-stream'
}

function isAllowedContactFile(file: File): boolean {
  if (CONTACT_ALLOWED_TYPES.has(file.type)) return true
  const ext = file.name.split('.').pop()?.toLowerCase()
  return Boolean(ext && CONTACT_EXT_TYPES[ext])
}

export async function uploadImage(
  file: File,
  path: string,
  preset: ImageUploadPreset = 'product'
): Promise<string> {
  const { optimizeImageForUpload, replacePathExtension } = await import(
    '@/lib/optimizeImage'
  )
  const optimized = await optimizeImageForUpload(file, preset)
  const storagePath = replacePathExtension(path, optimized)
  const storageRef = ref(storage, storagePath)
  await uploadBytes(storageRef, optimized, { contentType: optimized.type })
  return getDownloadURL(storageRef)
}

async function uploadContactFile(file: File, path: string): Promise<string> {
  const storageRef = ref(storage, path)
  const contentType = inferContactContentType(file)
  await uploadBytes(storageRef, file, { contentType })
  return getDownloadURL(storageRef)
}

/** Upload a single homepage banner image to Firebase Storage. */
export async function uploadBannerImage(
  file: File,
  bannerKey: string
): Promise<string> {
  const safeKey =
    bannerKey.replace(/[^a-zA-Z0-9-_]/g, '-').replace(/-+/g, '-') || 'banner'
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
  const path = `banners/${safeKey}/${Date.now()}.${ext}`
  return uploadImage(file, path, 'banner')
}

export async function uploadMultipleImages(
  files: File[],
  basePath: string
): Promise<string[]> {
  const uploads = files.map((file, i) => {
    const ext = file.name.split('.').pop() || 'jpg'
    const path = `${basePath}/${Date.now()}-${i}.${ext}`
    return uploadImage(file, path)
  })
  return Promise.all(uploads)
}

export function validateContactFiles(files: File[]): string | null {
  if (files.length > 5) return 'You can attach up to 5 files.'
  for (const file of files) {
    if (!isAllowedContactFile(file)) {
      return `"${file.name}" is not supported. Use images or PDF.`
    }
    if (file.size > CONTACT_MAX_FILE_BYTES) {
      return `"${file.name}" exceeds 5 MB.`
    }
  }
  return null
}

const BUG_REPORT_MAX_FILE_BYTES = 10 * 1024 * 1024
const BUG_REPORT_ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-m4v',
  'image/heic',
  'image/heif',
])

const BUG_REPORT_EXT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  webm: 'video/webm',
  mov: 'video/quicktime',
}

function inferBugReportContentType(file: File): string {
  if (file.type && BUG_REPORT_ALLOWED_TYPES.has(file.type)) {
    return file.type
  }
  if (file.type?.startsWith('image/')) return file.type
  if (file.type?.startsWith('video/')) return file.type
  const ext = file.name.split('.').pop()?.toLowerCase()
  const inferred = BUG_REPORT_EXT_TYPES[ext ?? '']
  if (!inferred) {
    throw new Error(`"${file.name}" has an unsupported file type.`)
  }
  return inferred
}

function isAllowedBugReportFile(file: File): boolean {
  if (BUG_REPORT_ALLOWED_TYPES.has(file.type)) return true
  const ext = file.name.split('.').pop()?.toLowerCase()
  return Boolean(ext && BUG_REPORT_EXT_TYPES[ext])
}

export function validateBugReportFiles(files: File[]): string | null {
  if (files.length > 3) return 'You can attach up to 3 files.'
  for (const file of files) {
    if (!isAllowedBugReportFile(file)) {
      return `"${file.name}" is not supported. Use images or video (MP4, WebM, MOV).`
    }
    if (file.size > BUG_REPORT_MAX_FILE_BYTES) {
      return `"${file.name}" exceeds 10 MB.`
    }
  }
  return null
}

const REVIEW_MAX_FILE_BYTES = 5 * 1024 * 1024

export function validateProductReviewImages(files: File[]): string | null {
  if (files.length > 3) return 'You can attach up to 3 photos.'
  for (const file of files) {
    if (!isSupportedImageFile(file)) {
      return `"${file.name}" is not a supported image.`
    }
    if (file.size > REVIEW_MAX_FILE_BYTES) {
      return `"${file.name}" exceeds 5 MB.`
    }
  }
  return null
}

export async function uploadProductReviewImages(
  files: File[],
  reviewId: string
): Promise<
  { name: string; url: string; contentType: string; size: number }[]
> {
  const error = validateProductReviewImages(files)
  if (error) throw new Error(error)

  const uploads = files.map(async (file, i) => {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80)
    const path = `product-reviews/${reviewId}/${Date.now()}-${i}-${safeName}`
    const url = await uploadImage(file, path, 'review')
    return {
      name: file.name,
      url,
      contentType: file.type || 'image/webp',
      size: file.size,
    }
  })
  return Promise.all(uploads)
}

export async function uploadBugReportAttachments(
  files: File[],
  reportId: string
): Promise<
  { name: string; url: string; contentType: string; size: number }[]
> {
  const error = validateBugReportFiles(files)
  if (error) throw new Error(error)

  const uploads = files.map(async (file, i) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'bin'
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80)
    const path = `bug-reports/${reportId}/${Date.now()}-${i}-${safeName}.${ext}`
    const contentType = inferBugReportContentType(file)
    const storageRef = ref(storage, path)
    await uploadBytes(storageRef, file, { contentType })
    const url = await getDownloadURL(storageRef)
    return {
      name: file.name,
      url,
      contentType,
      size: file.size,
    }
  })
  return Promise.all(uploads)
}

export async function uploadContactAttachments(
  files: File[],
  submissionId: string
): Promise<
  { name: string; url: string; contentType: string; size: number }[]
> {
  const error = validateContactFiles(files)
  if (error) throw new Error(error)

  const uploads = files.map(async (file, i) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'bin'
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80)
    const path = `contact-submissions/${submissionId}/${Date.now()}-${i}-${safeName}.${ext}`
    const contentType = inferContactContentType(file)
    const url = await uploadContactFile(file, path)
    return {
      name: file.name,
      url,
      contentType,
      size: file.size,
    }
  })
  return Promise.all(uploads)
}

export async function deleteImage(url: string): Promise<void> {
  try {
    const path = decodeURIComponent(
      url.split('/o/')[1]?.split('?')[0] || ''
    )
    if (path) {
      await deleteObject(ref(storage, path))
    }
  } catch {
    // Storage path extraction may fail for external URLs
  }
}
